import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { format } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { InvoiceDA } from '../../services/admin/invoice.service';
import { Invoice } from '../../models/invoice.model';
import { Company } from '../../models/company.model';
import { Settings } from '../../models/settings.model';
import { GlobalSwal } from '../../styles/swal.style';
import { getError } from '../../languages/translations/response';
import { modalHide } from '../../utils/modal.util';
import { formatMoney } from '../../scripts/format.value.script';
import { normalizeStatus } from '../../libraries/status.document.library';
import { normalizeContactData } from '../../scripts/format.value.script';
import { legendInvalid, legendValid } from '../../utils/legend.data.util';
import { expressions } from '../../libraries/regular.expressions.library';

import AppLegend from '../element/Legend';
import AppLoading from '../Loading';

export interface AppModalWriteOffInvoiceProps {
  companyForUser: Company | null,
  settingsForCompany: Settings | null,
  invoice: Invoice | null,
  setInvoice: Dispatch<SetStateAction<Invoice | null>>
};

const AppModalWriteOffInvoice: React.FunctionComponent<AppModalWriteOffInvoiceProps> = ({companyForUser, settingsForCompany, invoice, setInvoice}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [amount, setAmount] = useState({value: '', valid: false})
  const [date, setDate] = useState({value: '', valid: false})
  const [reason, setReason] = useState({value: '', valid: false})

  const debt = invoice ? Number(invoice.amount?.debt ?? 0) : 0
  const currencyCode = invoice?.currency?.code ?? 'CHF'

  useEffect(() => {
    if (invoice && debt > 0) {
      setAmount({value: debt.toFixed(2), valid: true})
      setDate({value: format(new Date(), 'yyyy-MM-dd'), valid: true})
      setReason({value: '', valid: false})
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice])

  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    const num = Number(val)
    const isValid = expressions.currency.test(val) && num > 0 && num <= debt
    setAmount({value: val, valid: isValid})
    if (isValid) { legendValid('write-off-legend-amount-required') } else { legendInvalid('write-off-legend-amount-required') }
  }

  const handleChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setDate({value: val, valid: val.length > 0})
    if (val.length > 0) { legendValid('write-off-legend-date-required') } else { legendInvalid('write-off-legend-date-required') }
  }

  const handleChangeReason = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setReason({value: val, valid: val.trim().length > 0})
    if (val.trim().length > 0) { legendValid('write-off-legend-reason-required') } else { legendInvalid('write-off-legend-reason-required') }
  }

  const subjectTax = settingsForCompany?.subject_tax ?? false
  const taxMethod = settingsForCompany?.tax_method ?? ''
  const showVatPhrase = subjectTax && taxMethod === 'agreed'

  const submitWriteOff = async () => {
    let valid = true

    if (!amount.valid) { legendInvalid('write-off-legend-amount-required'); valid = false }
    if (!date.valid) { legendInvalid('write-off-legend-date-required'); valid = false }
    if (!reason.valid) { legendInvalid('write-off-legend-reason-required'); valid = false }

    if (!valid || !companyForUser || !invoice) return

    setLoadIndicator('on')

    const response = await InvoiceDA.postWriteOff(companyForUser.id, invoice.id, {
      amount: Number(amount.value),
      date: date.value,
      reason: reason.value
    })

    if (response.status === 201 || response.status === 200) {
      const updated = response.data
      updated.status = normalizeStatus(updated.status)
      if (updated.payment_status) updated.payment_status = normalizeStatus(updated.payment_status)
      normalizeContactData(updated.customer)
      setInvoice(updated)

      modalHide('modal-write-off-invoice')

      GlobalSwal.fire({
        title: lang.labels.writeOffSuccess,
        icon: 'success',
        showConfirmButton: false,
        timer: 2000
      })
    } else {
      const errData = response.data
      GlobalSwal.fire({
        title: getError(errData?.code, lang.code, errData?.message),
        text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt
      })
    }

    setLoadIndicator('off')
  }

  return (
    <div id="modal-write-off-invoice" className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered mw-550px">
        <div className="modal-content">
          <div className="modal-header pb-0 border-0">
            <h3 className="modal-title">{lang.labels.writeOffInvoice}</h3>
            <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black"/>
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black"/>
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body scroll-y pt-3 pb-5 mx-5">
            <div className="mb-5">
              <label className="form-label required">{lang.labels.writeOffAmount} ({currencyCode})</label>
              <input type="text" className="form-control form-control-solid" value={amount.value} onChange={handleChangeAmount} />
              <div className="form-text text-muted">{lang.labels.maximum}: {formatMoney(debt)} {currencyCode}</div>
              <AppLegend component="write-off" attribute={{check: amount.valid, name: 'amount', index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
            </div>
            <div className="mb-5">
              <label className="form-label required">{lang.labels.writeOffDate}</label>
              <input type="date" className="form-control form-control-solid" value={date.value} onChange={handleChangeDate} />
              <AppLegend component="write-off" attribute={{check: date.valid, name: 'date', index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
            </div>
            <div className="mb-5">
              <label className="form-label required">{lang.labels.writeOffReason}</label>
              <textarea className="form-control form-control-solid" rows={3} value={reason.value} onChange={handleChangeReason} />
              <AppLegend component="write-off" attribute={{check: reason.valid, name: 'reason', index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
            </div>
            <div className="mb-5 p-4 bg-light-warning rounded">
              <div className="fw-bold text-gray-800">
                {lang.labels.writeOffConfirmMessage} {formatMoney(Number(amount.value) || 0)} {currencyCode}.
              </div>
              { showVatPhrase && invoice && Number(invoice.amount?.tax_total ?? 0) > 0 &&
                <div className="fw-bold text-gray-700 mt-2">
                  {lang.labels.writeOffVatCorrectionMessage.replace('{amount}', `${formatMoney(Number(invoice.amount.tax_total))} ${currencyCode}`)}
                </div>
              }
            </div>
            <div className="d-flex justify-content-end">
              <button className="btn btn-light me-3" type="button" data-bs-dismiss="modal">{lang.labels.cancel}</button>
              <button className="btn btn-danger" type="button" disabled={loadIndicator === 'on'} onClick={submitWriteOff}>
                { loadIndicator === 'off'
                  ? <span>{lang.labels.writeOff}</span>
                  : <span className="indicator-progress d-block"><AppLoading></AppLoading></span>
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalWriteOffInvoice;
